// /apps/patient/src/pages/progress/SessionDetailView.tsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import {
  ArrowLeft,
  Clock,
  Calendar,
  CheckCircle,
  Activity,
  AlertCircle
} from 'lucide-react';
import { PageHeader } from '../../components/layout/PageHeader';
import { patientApiService } from '../../services/api';
import { ExerciseCompletionCard, ExerciseCompletionData } from './components/ExerciseCompletionCard';

interface SessionDetail {
  id: string;
  session_date: string;
  status: 'completed' | 'partial' | 'in_progress';
  exercises_completed: number;
  exercises_skipped?: number;
  total_exercises_prescribed: number;
  duration_minutes?: number;
  prescription_name?: string;
  session_rating?: number;
  session_notes?: string;
  started_at?: string;
  completed_at?: string;
}

interface GroupedExercises {
  [moduleName: string]: ExerciseCompletionData[];
}

export function SessionDetailView() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<SessionDetail | null>(null);
  const [exerciseCompletions, setExerciseCompletions] = useState<ExerciseCompletionData[]>([]);

  useEffect(() => {
    if (sessionId) {
      fetchSessionDetail();
    }
  }, [sessionId]);

  const fetchSessionDetail = async () => {
    setLoading(true);
    try {
      const data = await patientApiService.getProgressSessionDetail(sessionId!);
      setSession(data.session);
      setExerciseCompletions(data.exercise_completions || []);
    } catch (error) {
      console.error('Error fetching session detail:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), 'EEEE, MMMM d, yyyy');
    } catch {
      return dateString;
    }
  };

  const formatTime = (dateString: string) => {
    try {
      return format(parseISO(dateString), 'h:mm a');
    } catch {
      return '';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <span className="flex items-center text-green-600 bg-green-50 px-3 py-1 rounded-full text-sm">
            <CheckCircle className="h-4 w-4 mr-1" />
            Completed
          </span>
        );
      case 'partial':
        return (
          <span className="flex items-center text-yellow-600 bg-yellow-50 px-3 py-1 rounded-full text-sm">
            <AlertCircle className="h-4 w-4 mr-1" />
            Partial
          </span>
        );
      default:
        return null;
    }
  };

  // Group exercises by module
  const groupedExercises: GroupedExercises = exerciseCompletions.reduce((acc, exercise) => {
    const moduleName = exercise.module_name || 'Exercises';
    if (!acc[moduleName]) {
      acc[moduleName] = [];
    }
    acc[moduleName].push(exercise);
    return acc;
  }, {} as GroupedExercises);

  const renderRatingStars = (rating: number) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <svg
            key={star}
            className={`w-5 h-5 ${star <= rating ? 'text-yellow-400' : 'text-gray-300'}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader title="Session Details" showBack />
        <div className="flex items-center justify-center h-64">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader title="Session Not Found" showBack />
        <div className="bg-white rounded-lg border p-8 text-center">
          <p className="text-gray-600">Unable to load session details.</p>
          <button
            onClick={() => navigate('/progress')}
            className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          >
            Back to Progress
          </button>
        </div>
      </div>
    );
  }

  const completionPercentage = session.total_exercises_prescribed > 0
    ? Math.round((session.exercises_completed / session.total_exercises_prescribed) * 100)
    : 0;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader title="Session Details" showBack />

      <div className="space-y-6">
        {/* Session Header Card */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {session.prescription_name || 'Workout Session'}
              </h2>
              <p className="text-gray-600 flex items-center mt-1">
                <Calendar className="h-4 w-4 mr-2" />
                {formatDate(session.session_date)}
              </p>
            </div>
            {getStatusBadge(session.status)}
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-sm text-gray-500">Exercises</p>
              <p className="text-lg font-semibold text-gray-900">
                {session.exercises_completed}/{session.total_exercises_prescribed}
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-sm text-gray-500">Completion</p>
              <p className="text-lg font-semibold text-gray-900">{completionPercentage}%</p>
            </div>

            {session.duration_minutes && (
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-500">Duration</p>
                <p className="text-lg font-semibold text-gray-900 flex items-center">
                  <Clock className="h-4 w-4 mr-1 text-gray-400" />
                  {session.duration_minutes} min
                </p>
              </div>
            )}

            {session.started_at && session.completed_at && (
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-500">Time</p>
                <p className="text-sm font-medium text-gray-900">
                  {formatTime(session.started_at)} - {formatTime(session.completed_at)}
                </p>
              </div>
            )}
          </div>

          {/* Session Rating */}
          {session.session_rating && (
            <div className="mt-6 pt-4 border-t">
              <p className="text-sm text-gray-500 mb-2">Session Rating</p>
              {renderRatingStars(session.session_rating)}
            </div>
          )}

          {/* Session Notes */}
          {session.session_notes && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm text-gray-500 mb-2">Session Notes</p>
              <p className="text-gray-700 bg-gray-50 rounded-lg p-3">{session.session_notes}</p>
            </div>
          )}
        </div>

        {/* Exercise Completions */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Activity className="h-5 w-5 mr-2 text-primary" />
            Exercise Details
          </h3>

          {exerciseCompletions.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              No exercise data recorded for this session.
            </p>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedExercises).map(([moduleName, exercises]) => (
                <div key={moduleName}>
                  {/* Only show module header if there are multiple modules */}
                  {Object.keys(groupedExercises).length > 1 && (
                    <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
                      {moduleName}
                    </h4>
                  )}
                  <div className="space-y-3">
                    {exercises.map((exercise, index) => (
                      <ExerciseCompletionCard
                        key={exercise.id || `${exercise.exercise_id}-${index}`}
                        exercise={exercise}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
